import { useEffect, useState } from 'react';
import { platform, type OrgSummary, type PlatformUser, type Role } from '../../api';

/* =========================================================================
 * Orgs page
 * ===================================================================== */

export function PlatformOrgsPage() {
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try { setOrgs((await platform.orgs.list()).orgs); }
    catch { setErr('Failed to load orgs'); }
  }
  useEffect(() => { refresh(); }, []);

  async function remove(o: OrgSummary) {
    if (!confirm(`Delete org "${o.name}"? This cascades to ALL devices, packages, playlists, schedules, and memberships in this org.`)) return;
    try {
      await platform.orgs.remove(o.id);
      refresh();
    } catch {
      setErr('Failed to delete org');
    }
  }

  return (
    <div>
      <div className="toolbar">
        <h1>Orgs</h1>
        <div className="spacer" />
        <button className="primary" onClick={() => setShowCreate(true)}>+ New org</button>
      </div>
      {err && <div className="error">{err}</div>}
      {orgs.length === 0 ? (
        <div className="empty">No orgs yet.</div>
      ) : (
        <table>
          <thead><tr><th>Slug</th><th>Name</th><th>Members</th><th>Devices</th><th>Packages</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.id}>
                <td style={{ fontFamily: 'monospace', color: 'var(--fg-3)' }}>{o.slug}</td>
                <td><strong>{o.name}</strong></td>
                <td>{o.member_count}</td>
                <td>{o.device_count}</td>
                <td>{o.package_count}</td>
                <td>{new Date(o.created_at).toLocaleDateString()}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="danger" onClick={() => remove(o)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showCreate && (
        <CreateOrgModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refresh(); }}
        />
      )}
    </div>
  );
}

function CreateOrgModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setErr(null); setBusy(true);
    try {
      await platform.orgs.create({ slug, name });
      onCreated();
    } catch (e: unknown) {
      const detail = ((e as { detail?: { error?: string } }).detail?.error);
      if (detail === 'slug_taken') setErr('That slug is already in use.');
      else setErr('Create failed.');
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>Create org</h2>
        <div className="field"><label>Slug (e.g. acme-corp)</label>
          <input value={slug} pattern="[a-z0-9-]+" autoFocus
            onChange={(e) => setSlug(e.target.value.toLowerCase())} required /></div>
        <div className="field"><label>Display name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required /></div>
        {err && <div className="error">{err}</div>}
        <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'flex-end' }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={!slug || !name || busy} onClick={submit}>
            {busy ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
 * Users page (global)
 * ===================================================================== */

export function PlatformUsersPage() {
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<PlatformUser | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try {
      const [u, o] = await Promise.all([platform.users.list(), platform.orgs.list()]);
      setUsers(u.users); setOrgs(o.orgs);
    } catch { setErr('Failed to load users'); }
  }
  useEffect(() => { refresh(); }, []);

  async function togglePlatformAdmin(u: PlatformUser) {
    try {
      await platform.users.update(u.id, { is_platform_admin: !u.is_platform_admin });
      refresh();
    } catch (e: unknown) {
      const detail = ((e as { detail?: { error?: string } }).detail?.error);
      setErr(detail === 'cannot_remove_last_platform_admin'
        ? 'Cannot remove the last platform admin.'
        : 'Failed.');
    }
  }
  async function toggleActive(u: PlatformUser) {
    try {
      await platform.users.update(u.id, { is_active: !u.is_active });
      refresh();
    } catch (e: unknown) {
      const detail = ((e as { detail?: { error?: string } }).detail?.error);
      setErr(detail === 'cannot_remove_last_platform_admin'
        ? 'Cannot deactivate the last platform admin.'
        : 'Failed.');
    }
  }

  return (
    <div>
      <div className="toolbar">
        <h1>Users</h1>
        <div className="spacer" />
        <button className="primary" onClick={() => setShowCreate(true)}>+ New user</button>
      </div>
      {err && <div className="error">{err}</div>}
      {users.length === 0 ? (
        <div className="empty">No users yet.</div>
      ) : (
        <table>
          <thead>
            <tr><th>Email</th><th>Name</th><th>Memberships</th><th>Platform admin</th><th>Status</th><th>Last login</th><th></th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td><strong>{u.email}</strong></td>
                <td>{u.name || '—'}</td>
                <td>
                  {u.memberships.length === 0 ? (
                    <span style={{ color: 'var(--fg-3)' }}>—</span>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3em' }}>
                      {u.memberships.map((m) => (
                        <span key={m.org_id} className="badge"
                          style={{ background: 'var(--bg-2)', color: 'var(--fg-2)' }}>
                          {m.org_slug} · {m.role}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td>
                  <input type="checkbox" checked={u.is_platform_admin}
                    onChange={() => togglePlatformAdmin(u)} style={{ width: 'auto' }} />
                </td>
                <td>
                  {u.is_active ? <span className="badge active">active</span> : <span className="badge offline">disabled</span>}
                </td>
                <td>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => setEditing(u)}>Edit</button>{' '}
                  <button className="danger" onClick={() => toggleActive(u)}>
                    {u.is_active ? 'Disable' : 'Enable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showCreate && (
        <CreateUserModal
          orgs={orgs}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refresh(); }}
        />
      )}
      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function CreateUserModal({
  orgs, onClose, onCreated,
}: { orgs: OrgSummary[]; onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [memberships, setMemberships] = useState<Array<{ org_id: string; role: Role }>>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function addMembership() {
    if (orgs.length === 0) return;
    setMemberships([...memberships, { org_id: orgs[0].id, role: 'editor' }]);
  }

  async function submit() {
    setErr(null); setBusy(true);
    try {
      await platform.users.create({ email, name, password, is_platform_admin: isPlatformAdmin, memberships });
      onCreated();
    } catch (e: unknown) {
      const detail = ((e as { detail?: { error?: string } }).detail?.error);
      if (detail === 'email_taken') setErr('Email already in use.');
      else setErr('Create failed.');
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ minWidth: 520 }}>
        <h2 style={{ marginTop: 0 }}>Create user</h2>
        <div className="field"><label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required /></div>
        <div className="field"><label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required /></div>
        <div className="field"><label>Temporary password (min 10 chars)</label>
          <input type="text" value={password} minLength={10} onChange={(e) => setPassword(e.target.value)} required /></div>
        <div className="field">
          <label>
            <input type="checkbox" style={{ width: 'auto', marginRight: '0.4em' }}
              checked={isPlatformAdmin} onChange={(e) => setIsPlatformAdmin(e.target.checked)} />
            Platform admin
          </label>
        </div>
        <label>Org memberships</label>
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '0.6em', marginBottom: '0.5em' }}>
          {memberships.length === 0 && (
            <div style={{ color: 'var(--fg-3)', textAlign: 'center', padding: '0.4em' }}>
              No org memberships. {isPlatformAdmin ? 'Platform admin can access any org.' : 'User won\'t be able to log in to any org.'}
            </div>
          )}
          {memberships.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.4em', marginBottom: '0.4em' }}>
              <select value={m.org_id} style={{ flex: 1 }}
                onChange={(e) => setMemberships(memberships.map((x, idx) => idx === i ? { ...x, org_id: e.target.value } : x))}>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.slug})</option>)}
              </select>
              <select value={m.role}
                onChange={(e) => setMemberships(memberships.map((x, idx) => idx === i ? { ...x, role: e.target.value as Role } : x))}>
                {(['owner','admin','editor','viewer'] as Role[]).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button className="danger" onClick={() => setMemberships(memberships.filter((_, idx) => idx !== i))}>×</button>
            </div>
          ))}
        </div>
        <button onClick={addMembership} disabled={orgs.length === 0}>+ Add membership</button>
        {err && <div className="error">{err}</div>}
        <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'flex-end', marginTop: '1em' }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={!email || !name || password.length < 10 || busy} onClick={submit}>
            {busy ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditUserModal({
  user, onClose, onSaved,
}: { user: PlatformUser; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(user.name || '');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setErr(null); setBusy(true);
    try {
      const patch: Partial<{ name: string; password: string }> = {};
      if (name !== (user.name || '')) patch.name = name;
      if (password) patch.password = password;
      if (Object.keys(patch).length === 0) { onClose(); return; }
      await platform.users.update(user.id, patch);
      onSaved();
    } catch { setErr('Update failed.'); }
    finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ minWidth: 460 }}>
        <h2 style={{ marginTop: 0 }}>Edit {user.email}</h2>
        <div className="field"><label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="field"><label>Reset password (optional, min 10 chars)</label>
          <input type="text" value={password} minLength={10}
            onChange={(e) => setPassword(e.target.value)} placeholder="leave blank to keep current" /></div>
        {err && <div className="error">{err}</div>}
        <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'flex-end' }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy} onClick={submit}>{busy ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
 * Stats page
 * ===================================================================== */

export function PlatformStatsPage() {
  const [stats, setStats] = useState<Awaited<ReturnType<typeof platform.stats>>['stats'] | null>(null);
  useEffect(() => { platform.stats().then((r) => setStats(r.stats)); }, []);

  if (!stats) return <div>Loading…</div>;

  const cards = [
    { label: 'Orgs', value: stats.orgs },
    { label: 'Users', value: stats.users },
    { label: 'Platform admins', value: stats.platform_admins },
    { label: 'Devices', value: stats.devices },
    { label: 'Packages', value: stats.packages },
    { label: 'Package versions', value: stats.package_versions },
    { label: 'Playlists', value: stats.playlists },
    { label: 'Schedules', value: stats.schedules },
  ];

  return (
    <div>
      <h1>Stats</h1>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1em' }}>
        {cards.map((c) => (
          <div key={c.label} className="card" style={{ textAlign: 'center', margin: 0 }}>
            <div style={{ color: 'var(--fg-3)', fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {c.label}
            </div>
            <div style={{ fontSize: '2em', fontWeight: 600, marginTop: '0.3em' }}>{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
